import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users,
  Package, ArrowUpRight, ArrowDownRight, Calendar, BarChart3
} from 'lucide-react';

const AdminAnalytics = () => {
  const stats = [
    {
      title: 'Revenue',
      value: '£24,562',
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600',
    },
    {
      title: 'Orders',
      value: '1,429',
      change: '+8.2%',
      trend: 'up',
      icon: ShoppingBag,
      color: 'bg-primary-500',
      lightColor: 'bg-primary-50',
      textColor: 'text-primary-600',
    },
    {
      title: 'Customers',
      value: '3,847',
      change: '+15.3%',
      trend: 'up',
      icon: Users,
      color: 'bg-green-500',
      lightColor: 'bg-green-50',
      textColor: 'text-green-600',
    },
    {
      title: 'Products Sold',
      value: '8,234',
      change: '-2.1%',
      trend: 'down',
      icon: Package,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      textColor: 'text-purple-600',
    },
  ];

  const topCategories = [
    { name: 'Rice & Grains', sales: 2340, revenue: '£12,450', percentage: 35 },
    { name: 'Spices & Masala', sales: 1890, revenue: '£8,920', percentage: 25 },
    { name: 'Tea & Coffee', sales: 1560, revenue: '£6,780', percentage: 20 },
    { name: 'Cooking Oils', sales: 890, revenue: '£5,230', percentage: 12 },
    { name: 'Snacks & Sweets', sales: 670, revenue: '£3,450', percentage: 8 },
  ];

  const recentActivity = [
    { action: 'New order placed', detail: 'Order #LS-2026-0001 - £45.32', time: '2 min ago', icon: ShoppingBag, color: 'bg-blue-500' },
    { action: 'New customer registered', detail: 'Arun Nair joined', time: '15 min ago', icon: Users, color: 'bg-green-500' },
    { action: 'Product restocked', detail: 'Ponni Boiled Rice - 100 units', time: '1 hour ago', icon: Package, color: 'bg-yellow-500' },
    { action: 'Payment received', detail: 'Order #LS-2026-0002 - £23.99', time: '2 hours ago', icon: DollarSign, color: 'bg-primary-500' },
    { action: 'Order delivered', detail: 'Order #LS-2026-0005 - £89.50', time: '3 hours ago', icon: ShoppingBag, color: 'bg-green-500' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 mt-1">Track your store performance and growth</p>
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm">
          <Calendar size={16} className="text-gray-400" />
          <span className="text-sm text-gray-600">Last 30 days</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.lightColor} rounded-xl flex items-center justify-center`}>
                <stat.icon size={24} className={stat.textColor} />
              </div>
              <span className={`flex items-center gap-1 text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                {stat.change}
              </span>
            </div>
            <p className="text-gray-500 text-sm">{stat.title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Categories */}
        <motion.div
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-lg text-gray-900">Top Categories</h2>
            <BarChart3 size={20} className="text-gray-400" />
          </div>
          <div className="space-y-5">
            {topCategories.map((cat, index) => (
              <div key={cat.name}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                  <span className="text-sm font-bold text-gray-900">{cat.revenue}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <motion.div
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2.5 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${cat.percentage}%` }}
                    transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">{cat.sales} sales</span>
                  <span className="text-xs text-gray-500">{cat.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-lg text-gray-900">Recent Activity</h2>
            <span className="text-sm text-primary-600 font-medium cursor-pointer hover:underline">View All</span>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <motion.div
                key={index}
                className="flex items-start gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <div className={`w-10 h-10 ${activity.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <activity.icon size={18} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{activity.detail}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{activity.time}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Chart Placeholder */}
      <motion.div
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-lg text-gray-900">Revenue Overview</h2>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-primary-500 rounded-full" />
            <span className="text-sm text-gray-500">Revenue</span>
            <span className="w-3 h-3 bg-secondary-500 rounded-full ml-3" />
            <span className="text-sm text-gray-500">Orders</span>
          </div>
        </div>
        <div className="h-80 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100">
          <div className="text-center">
            <BarChart3 size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Revenue Chart</p>
            <p className="text-sm text-gray-400 mt-1">Install recharts to enable interactive charts</p>
            <code className="text-xs bg-gray-100 px-3 py-1.5 rounded-lg mt-3 inline-block text-gray-600">
              npm install recharts
            </code>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminAnalytics;
